import { FC } from "react";
import { alpha, Box, Button, Dialog, Typography, useTheme } from "@mui/material";
import { CardGiftcard } from "@mui/icons-material";
import { keyframes } from "@mui/system";

// Modal de celebração do presente: abre uma vez por brinde (ver useGiftCelebration)
// e é o mesmo componente usado no Início e em Planos.

const pop = keyframes`
  0%   { transform: scale(.7) translateY(12px); opacity: 0; }
  60%  { transform: scale(1.04) translateY(0); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;

const hop = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
`;

const sparkle = keyframes`
  0%   { transform: translateY(8px) scale(.3); opacity: 0; }
  40%  { opacity: 1; }
  100% { transform: translateY(-22px) scale(1); opacity: 0; }
`;

type Props = {
  open: boolean;
  /** Nome do plano do brinde, quando a API já respondeu */
  planName?: string;
  loading?: boolean;
  onActivate: () => void;
  onClose: () => void;
};

/** Selo do presente: o mesmo ícone do botão flutuante, com faíscas em volta. */
const GiftSeal: FC = () => {
  const theme = useTheme();
  const gold = theme.palette.primary.main;
  const sparkles = [
    { left: "6%", top: 18, size: 7, delay: 0 },
    { left: "24%", top: 2, size: 5, delay: 0.35 },
    { left: "50%", top: -6, size: 8, delay: 0.7 },
    { left: "76%", top: 2, size: 5, delay: 0.2 },
    { left: "93%", top: 18, size: 7, delay: 0.55 },
  ];

  return (
    <Box sx={{ position: "relative", width: 150, height: 116, mx: "auto", mb: 2 }}>
      {sparkles.map((s, i) => (
        <Box
          key={i}
          aria-hidden
          sx={{
            position: "absolute",
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            borderRadius: "2px",
            transform: "rotate(45deg)",
            backgroundColor: i % 2 ? theme.palette.brand.creamStroke : gold,
            animation: `${sparkle} 1.8s ${s.delay}s ease-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.6 },
          }}
        />
      ))}

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: "calc(50% - 44px)",
          width: 88,
          height: 88,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.brand.cream,
          border: `2px solid ${gold}`,
          boxShadow: `0 10px 24px ${alpha(gold, 0.28)}`,
          animation: `${hop} 2.4s .4s ease-in-out infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <CardGiftcard sx={{ fontSize: 44, color: gold }} />
      </Box>
    </Box>
  );
};

export const GiftCelebrationDialog: FC<Props> = ({ open, planName, loading = false, onActivate, onClose }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 420,
          m: 2,
          p: { xs: "28px 20px 20px", sm: "32px 32px 24px" },
          borderRadius: "18px",
          textAlign: "center",
          backgroundImage: `radial-gradient(120% 90% at 50% 0%, ${theme.palette.brand.cream} 0%, ${theme.palette.background.paper} 62%)`,
          animation: `${pop} .38s ease-out`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        },
      }}
    >
      <GiftSeal />

      <Typography
        sx={{ fontFamily: "'DM Serif Display', serif", fontSize: 25, lineHeight: 1.15, color: "text.primary", mb: 1 }}
      >
        Você recebeu um presente!
      </Typography>

      <Typography sx={{ fontSize: "14.5px", color: "text.secondary", lineHeight: 1.6, mb: 3 }}>
        {planName ? (
          <>
            O plano <strong>{planName}</strong> está esperando por você. Quer ativar agora?
          </>
        ) : (
          "Tem um plano bônus esperando por você. Quer ativar agora?"
        )}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1.25, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ height: 46, px: 3, whiteSpace: "nowrap", color: "secondary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.divider}` }}
        >
          Agora não
        </Button>
        <Button variant="contained" onClick={onActivate} disabled={loading} sx={{ height: 46, px: 3, whiteSpace: "nowrap" }}>
          {loading ? "Ativando..." : "Resgatar presente"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default GiftCelebrationDialog;
